import { mediaUrl } from '@bluheadless/ui-logic/src/assets'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useComparableProducts } from '@bluheadless/ui-logic/src/hooks/product/useComparableProducts'
import { Badge, Box, Drawer, Fab, NoSsr, Zoom } from '@mui/material'
import isEqual from 'lodash/isEqual'
import router from 'next/router'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ListIcon from '../../atoms/icons/list-icon'
import LoadingIcon from '../../atoms/icons/loading-icon'
import XIcon from '../../atoms/icons/x-icon'
import Typography from '../../atoms/typography'
import Button from '../../molecules/button'
import TileComparable from '../../molecules/tile/comparable'
import RichContent from '../../particles/rich-content'
import {
	ActionCloseWrapper,
	AttributesWrapper,
	AttributesWrapper2,
	MuiGridNoProducts,
	SwatchStyled,
	TextSwatchStyled,
	TitleWrapper,
	WrapperAttributeCode,
	WrapperAttributeValue,
	WrapperContent,
} from './comparable-products.styled'

const renderAttributeValue = (product, attributeCode) => {
	const option = product?.configurableOptions?.find((item) => item.attributeCode === attributeCode)
	if (option) {
		if (option.renderer === 'text') {
			return option.values.map(({ label }, index) => <TextSwatchStyled key={index} value={label} />)
		} else {
			return option.values.map(({ hex, src, label, value }, index) => (
				<SwatchStyled key={index} label={label} value={value} hex={hex} src={src ? mediaUrl(src) : null} />
			))
		}
	} else if (product[attributeCode]) {
		return (
			<Typography variant="body1" component="span">
				<RichContent content={product[attributeCode]} />
			</Typography>
		)
	} else {
		return (
			<Typography variant="body1" component="span">
				<RichContent content={product.attributes[attributeCode]?.value} />
			</Typography>
		)
	}
}

const ComparableProducts = () => {
	const [open, setOpen] = useState(false)
	const { formatMessage } = useIntl()
	const [cachedComparableProducts, setCachedComparableProducts] = useState([])
	const { comparableProductsIds, comparableProducts, loading, removeItem } = useComparableProducts()

	const {
		catalog: {
			products: {
				comparable: { limit, attributes },
			},
		},
	} = useConfig()

	const handleOpen = (value) => {
		setOpen(value)
	}

	const transitionDuration = {
		enter: 200,
		exit: 200,
	}

	useEffect(() => {
		const onRouteChangeStart = () => {
			setOpen(false)
		}
		router.events.on('routeChangeStart', onRouteChangeStart)
		return () => {
			router.events.off('routeChangeStart', onRouteChangeStart)
		}
	}, [])

	useEffect(() => {
		// simulate a cached version of the contents
		// to prevent skeleton appearance every time user remove items.
		if (!loading && comparableProducts && !isEqual(comparableProducts, cachedComparableProducts)) {
			setCachedComparableProducts(comparableProducts)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [comparableProducts, loading])

	return (
		<NoSsr>
			<Box sx={{ position: 'fixed', bottom: '40px', left: '40px', zIndex: 1 }}>
				<Zoom
					in={cachedComparableProducts.length > 0}
					timeout={transitionDuration}
					style={{
						transitionDelay: `${comparableProductsIds.length ? transitionDuration.exit : 0}ms`,
					}}
					unmountOnExit
				>
					<Badge
						badgeContent={
							loading ? (
								<LoadingIcon fontSize="small" />
							) : cachedComparableProducts.length !== 0 ? (
								cachedComparableProducts.length
							) : null
						}
						overlap="circular"
						color="primary"
					>
						<Fab color="secondary" aria-label="add" size="medium" onClick={() => handleOpen(true)}>
							<ListIcon fontSize="middle" />
						</Fab>
					</Badge>
				</Zoom>
			</Box>
			<Drawer
				sx={{ overflow: 'hidden', zIndex: 11, height: '100%' }}
				anchor="bottom"
				open={open}
				onClose={() => handleOpen(false)}
				PaperProps={{
					sx: {
						height: '100%',
					},
				}}
			>
				<ActionCloseWrapper itemLength={cachedComparableProducts.length}>
					<Button
						variant="icon"
						Icon={XIcon}
						iconProps={{ fontSize: 'medium' }}
						color="black"
						onClick={() => handleOpen(false)}
					/>
				</ActionCloseWrapper>
				<TitleWrapper>
					<Typography variant="h3">
						<FormattedMessage id="comparable_title" />
					</Typography>
					<Typography variant="subtitle2">
						<FormattedMessage id="comparable_subtitle" /> {cachedComparableProducts.length} / {limit}
					</Typography>
				</TitleWrapper>
				<WrapperContent container>
					{cachedComparableProducts?.length > 0 && (
						<>
							<AttributesWrapper container wrap="nowrap">
								<WrapperAttributeCode />
								{cachedComparableProducts.map((product, index) => {
									return (
										<TileComparable
											key={index}
											variant="base"
											product={product}
											handleRemove={removeItem}
											loading={loading}
										/>
									)
								})}
							</AttributesWrapper>
							{attributes.map((attributeCode, index) => (
								<AttributesWrapper2 itemLength={cachedComparableProducts.length} key={index} container wrap="nowrap">
									<WrapperAttributeCode>
										<Typography variant="body2">
											{cachedComparableProducts[0].attributes[attributeCode]?.label
												? cachedComparableProducts[0].attributes[attributeCode].label
												: formatMessage({ id: 'comparable_attribute_' + attributeCode })}
										</Typography>
									</WrapperAttributeCode>

									{cachedComparableProducts.map((product, index) => (
										<WrapperAttributeValue key={index}>
											{renderAttributeValue(product, attributeCode)}
										</WrapperAttributeValue>
									))}
								</AttributesWrapper2>
							))}
						</>
					)}
					{cachedComparableProducts.length === 0 && (
						<MuiGridNoProducts item xs={12}>
							<Typography component="p" variant="subtitle2">
								<FormattedMessage id="comparable_no_products" />
							</Typography>
						</MuiGridNoProducts>
					)}
				</WrapperContent>
			</Drawer>
		</NoSsr>
	)
}

export default ComparableProducts
